import { COMPLIANCE_POLICY_TOOLS } from "./compliancePolicyToolsRegistry";

// One place to fix route names. Entries marked CONFIRM are guesses.
const ROUTES = {
  residents: "/residents",
  staff: "/staff",
  drills: "/emergency-evacuation-drills",
  licenseFee: "/license-fee",
  incidents: "/incidents",
  emar: "/resident-medications",
  vitalSigns: "/resident-vital-signs",
  carePlans: "/resident-care-plans#vital-sign-thresholds",
};

const toolByCode = Object.fromEntries(
  COMPLIANCE_POLICY_TOOLS.map((tool) => [tool.code, tool]),
);

const link = (path, label) => ({ path, label });
const capture = (hint, ...links) => ({ type: "link", hint, links });
const automatic = (message, ...links) => ({
  type: "notice",
  hint: message,
  links,
});

const ACTIONS = {
  // Resident records
  WA_AFH_020_TUBERCULOSIS_RECORDS: capture(
    "Record resident TB screening on each resident's TB Screening tab. Staff TB records have no capture screen yet.",
    link(ROUTES.residents, "Open residents"),
  ),
  WA_AFH_033_RESIDENT_ASSESSMENT_COMPLETENESS: capture(
    "Complete every required domain in the resident's assessment (Assessments tab).",
    link(ROUTES.residents, "Open residents"),
  ),
  WA_AFH_034_ASSESSMENT_CURRENCY_REASSESSMENT: capture(
    "Reassess annually and after a material change, from the resident's Assessments tab.",
    link(ROUTES.residents, "Open residents"),
  ),
  WA_AFH_035_PRELIMINARY_SERVICE_PLAN: capture(
    "Add a preliminary service plan for each newly admitted resident.",
    link(ROUTES.residents, "Open residents"),
  ),
  WA_AFH_036_NEGOTIATED_CARE_PLAN_EXISTENCE: capture(
    "Start and confirm a negotiated care plan for each resident.",
    link(ROUTES.residents, "Open residents"),
  ),
  WA_AFH_037_NCP_COMPLETENESS: capture(
    "Fill in goals, interventions, preferences, responsibilities and risks on each NCP.",
    link(ROUTES.residents, "Open residents"),
  ),
  WA_AFH_038_ASSESSMENT_TO_NCP_CONSISTENCY: capture(
    "Update the NCP whenever a newer assessment exists, and review it at least every 12 months.",
    link(ROUTES.residents, "Open residents"),
  ),
  WA_AFH_039_CONDITION_CHANGE_TO_NCP_UPDATE: capture(
    "After a significant change, update the NCP and resolve the change record.",
    link(ROUTES.residents, "Open residents"),
  ),
  WA_AFH_042_RESIDENT_RECORD_CONTENTS: capture(
    "Complete each resident's record: contacts, medical history, medication list, assessment and plans.",
    link(ROUTES.residents, "Open residents"),
  ),
  WA_AFH_043_LEGAL_AUTHORITY_DOCUMENTS: capture(
    "Upload guardianship or POA documents on the resident's Documents tab.",
    link(ROUTES.residents, "Open residents"),
  ),
  WA_AFH_063_TRANSFER_DISCHARGE_PROCESS: capture(
    "Record the discharge date on the resident record when a resident is discharged.",
    link(ROUTES.residents, "Open residents"),
  ),

  // Staff records
  WA_AFH_007_CAREGIVER_QUALIFICATIONS: capture(
    "Caregivers submit work authorization from their Care Profile. Verification is recorded on the staff profile.",
    link(ROUTES.staff, "Open staff"),
  ),
  WA_AFH_012_BACKGROUND_CHECK_VALIDITY_RENEWAL: capture(
    "Keep each caregiver's background-check verification current on their staff profile.",
    link(ROUTES.staff, "Open staff"),
  ),
  WA_AFH_022_STAFF_CONTACT_INFORMATION: capture(
    "Keep each staff member's role, address and emergency contact current on their Care Profile.",
    link(ROUTES.staff, "Open staff"),
  ),

  // Drills
  WA_AFH_066_PARTIAL_EVACUATION_DRILL_FREQUENCY: capture(
    "Log each partial drill with participants, time and documentation.",
    link(ROUTES.drills, "Open drills"),
  ),
  WA_AFH_067_RESIDENT_DRILL_PARTICIPATION: capture(
    "Record which residents took part in each partial drill.",
    link(ROUTES.drills, "Open drills"),
  ),
  WA_AFH_069_EVACUATION_TIME: capture(
    "Record the evacuation time for every drill.",
    link(ROUTES.drills, "Open drills"),
  ),
  WA_AFH_070_EVACUATION_DRILL_DOCUMENTATION: capture(
    "Complete every documentation field on each conducted drill.",
    link(ROUTES.drills, "Open drills"),
  ),

  // Licensing
  WA_AFH_002_LICENSED_CAPACITY_OCCUPANCY: capture(
    "Occupancy is counted automatically from active residents. The licensed bed count comes from the license record.",
    link(ROUTES.licenseFee, "Open license record"),
  ),

  // Background evaluation from normal work
  WA_AFH_045_MEDICATION_ORDER_PRESENT_AND_CURRENT: automatic(
    "Evaluated automatically from eMAR data. Keep each medication's order details current.",
    link(ROUTES.emar, "Open eMAR"),
  ),
  WA_AFH_047_DAILY_MEDICATION_LOG_EMAR: automatic(
    "Evaluated automatically from eMAR administrations. No separate entry is needed.",
    link(ROUTES.emar, "Open eMAR"),
  ),
  WA_AFH_048_MISSED_MEDICATION_DOCUMENTATION: automatic(
    "Evaluated automatically. Record a reason for every missed dose in eMAR.",
    link(ROUTES.emar, "Open eMAR"),
  ),
  WA_AFH_049_MEDICATION_REFUSAL_DOCUMENTATION: automatic(
    "Evaluated automatically. Record a reason for every refusal in eMAR.",
    link(ROUTES.emar, "Open eMAR"),
  ),
  WA_AFH_054_PRN_MEDICATION_DOCUMENTATION: automatic(
    "Evaluated automatically. Keep a PRN reason on each PRN medication and administer PRNs through eMAR.",
    link(ROUTES.emar, "Open eMAR"),
  ),
  WA_AFH_087_HEALTH_MONITORING_ORDERED_REQUIRED: automatic(
    "Evaluated automatically from vital sign readings. Set monitoring thresholds under Care Plans, then keep capturing readings.",
    link(ROUTES.vitalSigns, "Open vital signs"),
    link(ROUTES.carePlans, "Open care plans"),
  ),
};

// Hybrid incident policies: record incidents AND submit the periodic questionnaire.
[
  "WA_AFH_026_INCIDENT_LOG",
  "WA_AFH_027_ABUSE_NEGLECT_EXPLOITATION_ABANDONMENT_REPORTING",
  "WA_AFH_028_EVACUATION_MISSING_RESIDENT_CONTINUITY_EVENT_REP",
  "WA_AFH_029_SIGNIFICANT_CONDITION_CHANGE_SERIOUS_INJURY_TRAUMA_DEATH_NOTIFICATIONS",
].forEach((code) => {
  ACTIONS[code] = capture(
    "Log every incident, then submit the periodic questionnaire confirming nothing was left out.",
    link(ROUTES.incidents, "Open incidents"),
    ...(toolByCode[code]
      ? [link(toolByCode[code].path, "Open questionnaire")]
      : []),
  );
});

export const resolvePolicyAction = (code) => {
  if (ACTIONS[code]) return ACTIONS[code];
  const tool = toolByCode[code];
  if (tool)
    return capture(tool.description, link(tool.path, `Open ${tool.label}`));
  return null;
};
